import React, { useState, useEffect } from 'react';
import PinPad from './PinPad';
import logo from '../V Clinic logo - Blue gradient.PNG'; 

const PortalGate = ({ onLoginSuccess }) => {
    const [step, setStep] = useState('BRANCH');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState('');
    
    // --- 🛰️ REAL DATA STATES ---
    const [branches, setBranches] = useState([]); 
    const [doctors, setDoctors] = useState([]); 
    const [virtualStaff, setVirtualStaff] = useState([]); // 🆕 เพิ่มถังเก็บข้อมูล Virtual Staff
    const [loading, setLoading] = useState(true);
    const [lockoutError, setLockoutError] = useState(''); 

    // --- 🛰️ FETCH DATA FROM ODOO ---
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // 🚀 ดึงข้อมูล 3 ท่อพร้อมกันรวมถึงโมเดลพนักงานเสมือนตัวใหม่
                const [branchRes, doctorRes, staffRes] = await Promise.all([
                    fetch('/api/branches'),
                    fetch('/api/doctors'),
                    fetch('/api/staff').catch(() => ({ json: () => [] })) // กันเหนียวถ้า API หลังบ้านยังไม่เสร็จ
                ]);
                
                let branchData = await branchRes.json();
                const doctorData = await doctorRes.json();
                const staffData = await staffRes.json();
                
                // 🪄 CUSTOM BRANCH SORTER
                branchData = branchData.sort((a, b) => {
                    const getRank = (name) => {
                        if (name.includes('Central')) return 5;
                        if (name.includes('Chiangrai')) return 1;
                        if (name.includes('Thonglor')) return 2;
                        if (name.includes('Bangna')) return 3;
                        if (name.includes('Emporium')) return 4;
                        return 99;
                    };
                    return getRank(a.name) - getRank(b.name);
                });
                
                setBranches(branchData);
                setDoctors(doctorData); 
                setVirtualStaff(staffData); // จัดเก็บสตาฟชุดใหม่เข้าสเตต
            } catch (err) {
                console.error("Data Load Error:", err);
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
    }, []);

    const getBranchColor = (index) => {
        const colors = ['#d8ecfa', '#fae5ec', '#f7f4e0', '#dbf5db', '#f4e0f8'];
        return colors[index % colors.length];
    };

    const handleVerifyPin = async (pin) => {
        setLockoutError(''); 
        try {
            // 🛡️ SURGICAL ROUTING: เช็คสถานะว่าเป็นสตาฟจำแลงหรือคุณหมอจริง 
            // เพื่อแยกเส้นทางยิงตรวจสอบ โดยไม่รบกวนลอจิกเดิมเด็ดขาด
            const isVirtual = selectedDoctor.is_virtual_staff;
            const targetEndpoint = isVirtual ? '/api/odoo/verify-staff-pin' : '/api/odoo/verify-pin';

            const response = await fetch(targetEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    user_id: selectedDoctor.id, 
                    pin: pin,
                    branch_code: selectedBranch.code || null // แนบโค้ดสาขาไปด้วยเพื่อความแม่นยำ
                })
            });

            const result = await response.json();

            // 🛑 THE KICKOUT (ระบบเตะเซสชันซ้อน)
            if (result.status === 'blocked') {
                setLockoutError(result.message || 'บัญชีเครื่องนี้กำลังเปิดใช้งานอยู่บน iPad เครื่องอื่น!');
                return; 
            }

            // ✅ SUCCESS
            if (result.success) {
                localStorage.setItem('user_branch', selectedBranch.name); 
                localStorage.setItem('user_branch_id', selectedBranch.id); 
                localStorage.setItem('doctor_name', selectedDoctor.nickname);
                localStorage.setItem('doctor_id', selectedDoctor.id); 
                localStorage.setItem('session_token', result.token); 
                localStorage.setItem('user_role', result.role); // สตาฟจะได้ค่า 'frontdesk' ไปทำงานต่อ
                
                onLoginSuccess();
            } else {
                alert(result.message || 'PIN ไม่ถูกต้อง กรุณาลองใหม่');
            }
        } catch (err) {
            console.error(err);
            alert('เกิดข้อผิดพลาดในการเชื่อมต่อ Odoo');
        }
    };

    // 🧠 SPLIT THE USERS BASED ON ROLE
    const realDoctors = doctors.filter(d => d.role !== 'frontdesk');
    
    // 🤝 THE COLLABORATION GANG: ควบรวมทั้งพนักงาน Frontdesk เดิม และเพื่อนใหม่พนักงานเสมือนเข้าด้วยกัน
    const mappedVirtualStaff = virtualStaff
        .filter(s => s.branch_id === selectedBranch.id)
        .map(s => ({
            id: s.id,
            nickname: s.name, // เช่น VCR1, VCR2
            name: 'จุดบริการประจำเครื่อง',
            role: 'frontdesk',
            branch_id: s.branch_id,
            is_virtual_staff: true // 🔥 แปะป้ายบอกหน้าระบบว่าเป็นบัญชีจำแลงประหยัดงบ
        }));

    const frontDeskStaff = [
        ...doctors.filter(d => d.role === 'frontdesk' && d.branch_id === selectedBranch.id),
        ...mappedVirtualStaff
    ];

    return (
        <div style={styles.overlay}>
            <div style={styles.container}>
                <p style={styles.topText}>OPD System of</p>
                <img src={logo} alt="V Clinic" style={styles.logo} />

                {/* --- 🛑 THE PENALTY BOX --- */}
                {lockoutError && (
                    <div style={styles.lockoutBox}>
                        <h3 style={styles.lockoutTitle}>⚠️ ปฏิเสธการเข้าถึง (Access Denied)</h3>
                        <p style={styles.lockoutText}>{lockoutError}</p>
                        <button style={styles.lockoutBtn} onClick={() => {
                            setLockoutError('');
                            setStep('DOCTOR'); 
                        }}>
                            รับทราบ
                        </button>
                    </div>
                )}

                {/* --- PAGE 1: BRANCH SELECTION --- */}
                {step === 'BRANCH' && !lockoutError && (
                    <>
                        <h2 style={styles.title}>เลือกสาขา (Select Branch)</h2>
                        {loading ? (
                            <p>Loading clinic branches...</p>
                        ) : (
                            <div style={styles.doctorGrid}> 
                                {branches.map((b, index) => (
                                    <button 
                                        key={b.id} 
                                        style={{...styles.card, background: getBranchColor(index)}} 
                                        onClick={() => { 
                                            setSelectedBranch(b);
                                            setStep('DOCTOR'); 
                                        }}
                                    >
                                        <h3 style={styles.cardTitle}>{b.name}</h3>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* --- PAGE 2: USER SELECTION (SPLIT UI) --- */}
                {step === 'DOCTOR' && !lockoutError && (
                    <>
                        <h2 style={styles.title}>เลือกผู้ใช้งาน (Select User)</h2>
                        
                        {/* 👨‍⚕️ DOCTORS SECTION */}
                        {realDoctors.length > 0 && (
                            <div style={styles.sectionContainer}>
                                <div style={{...styles.sectionHeader, color: '#1976d2', borderBottomColor: '#1976d2'}}>
                                    👨‍⚕️ แพทย์ผู้ตรวจ (Doctors)
                                </div>
                                <div style={styles.doctorTwoColumnGrid}>
                                    {realDoctors.map(d => (
                                        <button key={d.id} style={styles.doctorCardHorizontal} onClick={() => { setSelectedDoctor(d); setStep('PIN'); }}>
                                            {d.image ? (
                                                <img src={d.image} alt={d.nickname} style={styles.doctorImageSmall} />
                                            ) : (
                                                <div style={styles.emojiPlaceholder}>👨‍⚕️</div>
                                            )}
                                            <div style={styles.doctorInfo}>
                                                <h3 style={styles.nicknameText}>{d.nickname}</h3>
                                                <p style={styles.fullNameText}>{d.name}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 🏢 FRONT DESK SECTION */}
                        {frontDeskStaff.length > 0 && (
                            <div style={{...styles.sectionContainer, background: '#f8fbf8', border: '1px solid #e8f5e9', padding: '20px', borderRadius: '16px'}}>
                                <div style={{...styles.sectionHeader, color: '#2e7d32', borderBottomColor: '#2e7d32'}}>
                                    🏢 แผนกต้อนรับ (Front Desk)
                                </div>
                                <div style={styles.doctorTwoColumnGrid}>
                                    {frontDeskStaff.map(d => (
                                        <button key={d.is_virtual_staff ? `v-${d.id}` : d.id} style={{...styles.doctorCardHorizontal, border: '1px solid #c8e6c9'}} onClick={() => { setSelectedDoctor(d); setStep('PIN'); }}>
                                            <div style={{...styles.emojiPlaceholder, background: '#e8f5e9', borderRadius: '50%', width: '55px', height: '55px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px'}}>
                                                🧑‍💻
                                            </div>
                                            <div style={styles.doctorInfo}>
                                                <h3 style={styles.nicknameText}>{d.nickname}</h3>
                                                <p style={styles.fullNameText}>{d.name}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button onClick={() => setStep('BRANCH')} style={styles.backLink}>← กลับไปเลือกสาขา</button>
                    </>
                )}
                
                {/* --- PAGE 3: PIN PAD --- */}
                {step === 'PIN' && !lockoutError && (
                    <>
                        <h2 style={styles.title}>
                            ยืนยันตัวตน: {selectedDoctor?.nickname || 'User'} 
                        </h2>
                        
                        <p style={{marginBottom: '20px', color: '#666'}}>
                            สาขา {selectedBranch?.name || 'Unknown'} 
                        </p>
                        
                        <PinPad 
                            onComplete={handleVerifyPin} 
                            onCancel={() => setStep('DOCTOR')} 
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// --- UPDATED STYLES ---
const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#f4f7f9', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', overflowY: 'auto', padding: '20px 0' },
    container: { background: '#fff', padding: '40px', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.1)', textAlign: 'center', width: '95%', maxWidth: '850px', margin: 'auto' },
    topText: { margin: 0, fontSize: '14px', color: '#888', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase' },
    logo: { width: '200px', marginBottom: '30px' },
    title: { fontSize: '24px', color: '#333', marginBottom: '30px' },
    doctorGrid: { display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', maxWidth: '850px', margin: '0 auto 30px auto' },
    card: { flex: '0 1 240px', padding: '30px', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '15px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' },
    cardTitle: { margin: 0, fontSize: '20px', color: '#444', fontWeight: '600' },
    backLink: { background: 'none', border: 'none', color: '#2196F3', cursor: 'pointer', fontSize: '16px', marginTop: '20px' },
    
    // UI Split Styles
    sectionContainer: { marginBottom: '35px', textAlign: 'left' },
    sectionHeader: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', paddingBottom: '10px', borderBottom: '3px solid', display: 'inline-block' },
    
    doctorTwoColumnGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    doctorCardHorizontal: { display: 'flex', alignItems: 'center', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '16px', background: '#fff', cursor: 'pointer', textAlign: 'left', transition: 'transform 0.2s', width: '100%' },
    doctorImageSmall: { width: '55px', height: '55px', borderRadius: '50%', objectFit: 'cover', marginRight: '15px', border: '2px solid #e3f2fd' },
    emojiPlaceholder: { fontSize: '30px', marginRight: '15px' },
    doctorInfo: { display: 'flex', flexDirection: 'column' },
    nicknameText: { margin: 0, fontSize: '18px', color: '#333', fontWeight: '600' },
    fullNameText: { margin: 0, fontSize: '12px', color: '#999' },
    
    // Brutal Lockout UI
    lockoutBox: { background: '#ffebee', border: '3px solid #d32f2f', padding: '30px', borderRadius: '16px', margin: '20px auto', maxWidth: '600px', textAlign: 'center', boxShadow: '0 10px 25px rgba(211, 47, 47, 0.2)' },
    lockoutTitle: { color: '#b71c1c', margin: '0 0 15px 0', fontSize: '22px', fontWeight: 'bold' },
    lockoutText: { color: '#c62828', fontSize: '18px', margin: '0 0 25px 0', lineHeight: '1.6', fontWeight: '500' },
    lockoutBtn: { background: '#d32f2f', color: '#fff', border: 'none', padding: '12px 30px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', textTransform: 'uppercase' }
};

export default PortalGate;